import { useQuery } from '@apollo/client'
import { CircularProgress, Typography, makeStyles } from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { GET_KNOWLEDGE_BASE_PROJECT_INFO } from 'api/graphQL/queries/specIntelligence.queries'
import {
    GetKnowledgeBaseProjectInfoInput,
    GetKnowledgeBaseProjectInfoResponse,
    SectionListEntry,
} from 'api/graphQL/types/specIntelligence.types'
import SelectionTree, {
    SelectionTreeNode,
    getAllLeafIds,
} from 'components/SelectionTree/SelectionTree'
import React, { useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles(() => ({
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    emptyMessage: {
        fontSize: pxToRem(13),
        color: '#757575',
        textAlign: 'center',
        padding: `0 ${pxToRem(16)}`,
    },
}))

/** Props for {@link DivisionSectionTree}. */
export interface DivisionSectionTreeProps {
    /** Currently selected leaf node IDs (projectProductFamilyId values). */
    selectedLeafIds: string[]
    /** Called when the user changes their selection. */
    onSelectionChange: (ids: string[]) => void
    /** Called once when section data loads, with the full list of leaf IDs. */
    onNodesLoaded: (allLeafIds: string[]) => void
    /**
     * Section numbers (e.g. `['07 26 00', '03 30 00']`) to pre-select when data
     * first loads. Matched via each leaf node's `badge` (= the section number string).
     */
    initialSectionNumbers?: string[]
    /**
     * When `true`, after pre-selection is applied the tree scrolls its nearest
     * scrollable ancestor to put the first selected row at the top of the viewport.
     * Default `false`.
     */
    scrollToFirstSelected?: boolean
}

const buildSectionNumberToIdMap = (treeNodes: SelectionTreeNode[]): Map<string, string> => {
    const map = new Map<string, string>()
    for (const divNode of treeNodes) {
        for (const leaf of divNode.children ?? []) {
            if (leaf.badge) map.set(leaf.badge, leaf.id)
        }
    }
    return map
}

const buildNodes = (sections: SectionListEntry[]): SelectionTreeNode[] => {
    const byDivision = new Map<string, { name: string; sections: SectionListEntry[] }>()
    for (const s of sections) {
        if (!byDivision.has(s.divisionNumber)) {
            byDivision.set(s.divisionNumber, { name: s.divisionName, sections: [] })
        }
        byDivision.get(s.divisionNumber)!.sections.push(s)
    }
    return Array.from(byDivision.entries()).map(([divNum, { name, sections: divSections }]) => ({
        id: `div-${divNum}`,
        badge: `DIV ${divNum}`,
        label: name.toUpperCase(),
        children: divSections.map(s => ({
            id: s.projectProductFamilyId,
            badge: s.sectionNumber,
            label: s.sectionName.toUpperCase(),
        })),
    }))
}

/**
 * Fetches project sections via `getKnowledgeBaseProjectInfo` and renders them
 * as a CSI division/section selection tree. Shows a spinner while loading and
 * an empty-state message when the project has no sections.
 */
const DivisionSectionTree = ({
    selectedLeafIds,
    onSelectionChange,
    onNodesLoaded,
    initialSectionNumbers,
    scrollToFirstSelected = false,
}: DivisionSectionTreeProps) => {
    const classes = useStyles()
    const { projectId } = useParams<{ projectId: string }>()

    const { data, loading, error } = useQuery<
        GetKnowledgeBaseProjectInfoResponse,
        GetKnowledgeBaseProjectInfoInput
    >(GET_KNOWLEDGE_BASE_PROJECT_INFO, {
        variables: { input: { projectId } },
        fetchPolicy: 'network-only',
        skip: !projectId,
    })

    const nodes = useMemo<SelectionTreeNode[]>(() => {
        const sections = data?.getKnowledgeBaseProjectInfo?.sections ?? []
        return buildNodes(sections)
    }, [data])

    const didPreSelectRef = useRef(false)

    useEffect(() => {
        if (loading) return
        onNodesLoaded(getAllLeafIds(nodes))
        if (didPreSelectRef.current || !initialSectionNumbers?.length) return
        didPreSelectRef.current = true
        const sectionNumberToId = buildSectionNumberToIdMap(nodes)
        const ids = initialSectionNumbers
            .map((sn) => sectionNumberToId.get(sn))
            .filter((id): id is string => Boolean(id))
        if (ids.length) onSelectionChange(ids)
    }, [nodes, loading, onNodesLoaded, onSelectionChange, initialSectionNumbers])

    if (loading) {
        return (
            <div className={classes.centered}>
                <CircularProgress size={32} />
            </div>
        )
    }

    if (error) {
        return (
            <div className={classes.centered}>
                <Typography className={classes.emptyMessage}>
                    Unable to load sections. Please try again.
                </Typography>
            </div>
        )
    }

    if (nodes.length === 0) {
        return (
            <div className={classes.centered}>
                <Typography className={classes.emptyMessage}>
                    No sections found in this project.
                </Typography>
            </div>
        )
    }

    return (
        <SelectionTree
            nodes={nodes}
            selectedLeafIds={selectedLeafIds}
            onSelectionChange={onSelectionChange}
            defaultExpandAll
            scrollToFirstSelected={scrollToFirstSelected}
        />
    )
}

export default DivisionSectionTree
